import { camelCase } from "lodash";
import type {
  PnlCategoryObjOfObjectsOfNumbers,
  PnlsOrTagsWithMeasurementObj,
  RollupWithTagsAndCountsAndRange,
  PnlChartSectionType,
  PnlCategoryObjOfTagTimes,
  DataDumpAggregateType,
} from "types/types";
import { PnlCategoryLabelsEnum, PnlCategoriesEnum } from "types/enums";
import { sumArray } from "utils/arrayUtils";
import { objectFromEntries } from "@raven/shared/utils";
import {
  PNL_COLORS,
  SORTED_STANDARD_PNL_CATEGORIES,
} from "constants/constants";

export const CATEGORIES_NOT_AFFECTING_BAR_WIDTHS = [
  "notScheduled",
  "plantLoss",
  "uncollected",
  // "producing" is accounted for in the function,
  // depending if it's a "producing tags" chart
];
const CATEGORIES_NOT_AFFECTING_PERCENTS = ["notScheduled", "uncollected"];

const getTagsObjectBase = (): PnlCategoryObjOfTagTimes => ({
  producing: {},
  planned: {},
  unplanned: {},
  waiting: {},
  unknown: {},
  notScheduled: {},
  uncollected: {},
});

/**
 * Returns an array of rows (PnlChartSectionType) describing each P&L category:
 * - Each row has summary statistics (percent, count...) to display in the row header;
 * - Each row also has a 'bars' array, one bar for each tag, with its own summary statistics.
 * - The bar widths are calculated relative to the maximum width that isn't in the producing, uncollected, or NPS categories
 *
 * @param {RollupWithTagsAndCountsAndRange} rollup
 * @param {PnlCategoriesEnum[]} hiddenCategories
 * @returns {PnlChartSectionType[]}
 */
export const calcPnlChartCategories = (
  rollup: RollupWithTagsAndCountsAndRange,
  hiddenCategories: PnlCategoriesEnum[],
  /** if we're passed the DDA, we'll pull extra pnl columns "numberOfEvents" (Freq.) and "averageDurationMs" (Avg.) from it into the pnl chart */
  dataDumpFullRange?: DataDumpAggregateType | null
  // tslint:disable-next-line: no-big-function
): PnlChartSectionType[] => {
  if (!rollup || Object.keys(rollup).length === 0) {
    return [];
  }
  // show producing tags if we have them
  // this also modifies the denominator for calculating bar widths
  // (producing tags should never overflow >100%)
  const isChartWithProducingTags =
    rollup?.tags?.producing && Object.entries(rollup.tags.producing).length > 0;

  const taggedProducingTime = isChartWithProducingTags
    ? sumArray(Object.values(rollup.tags.producing))
    : 0;

  const isChartWithUnknownTags =
    rollup?.tags?.unknown && Object.entries(rollup.tags.unknown).length > 0;

  const taggedUnknownTime = isChartWithUnknownTags
    ? sumArray(Object.values(rollup.tags.unknown))
    : 0;

  // calculate total time, tagged and untagged, to scale the bar widths
  const untaggedProducingTime = !rollup.producing
    ? 0
    : rollup.producing - taggedProducingTime;
  const untaggedUnknownTime = !rollup.unknown
    ? 0
    : rollup.unknown - taggedUnknownTime;
  const timeNoTags = untaggedUnknownTime + untaggedProducingTime;

  // e.g. {producing: {}, unplanned: {Bundler: 198495163}, ...}
  const categoriesWithTagsTimes: PnlCategoryObjOfTagTimes = Object.entries(
    rollup.tags
  ).reduce((acc, [category, tagsObj]) => {
    const nextAcc = { ...acc };
    if (
      Object.values(PnlCategoriesEnum).includes(category as PnlCategoriesEnum)
    ) {
      Object.entries(tagsObj).forEach(([tagName, tagTime]) => {
        if (tagName in nextAcc[category]) {
          nextAcc[category][tagName] += tagTime;
        } else {
          nextAcc[category][tagName] = tagTime;
        }
      });
    }
    return nextAcc;
  }, getTagsObjectBase());

  const totalTime =
    // sum of all tag times
    // except no production scheduled & uncollected
    timeNoTags +
    sumArray(
      Object.values(
        Object.entries(categoriesWithTagsTimes).reduce(
          (acc, [category, tagObj]) => [
            ...acc,
            ...(CATEGORIES_NOT_AFFECTING_PERCENTS.includes(category)
              ? []
              : Object.values(tagObj)),
          ],
          [] as number[]
        )
      )
    );

  // divide all tag times by total time
  const tagPercents: PnlCategoryObjOfObjectsOfNumbers = !totalTime
    ? categoriesWithTagsTimes
    : (objectFromEntries(
        Object.entries(categoriesWithTagsTimes).map(([category, tagsObj]) => {
          const tagsPercentsObj = objectFromEntries(
            Object.entries(tagsObj).map(([tagName, tagTime]) => [
              tagName,
              totalTime === 0 ? 0 : tagTime / totalTime,
            ])
          );
          return [category, tagsPercentsObj];
        })
      ) as PnlCategoryObjOfObjectsOfNumbers);

  const tagPercentsWithUnknownUncoll: PnlCategoryObjOfObjectsOfNumbers = {
    ...getTagsObjectBase(),
    ...tagPercents,
    unknown: {
      Unknown:
        totalTime === 0 || !rollup.unknown ? 0 : rollup.unknown / totalTime,
    },
    uncollected: {
      Uncollected:
        totalTime === 0 || !rollup.uncollected
          ? 0
          : rollup.uncollected / totalTime,
    },
  };

  const tagPercentsWithProdUnknownUncoll: PnlCategoryObjOfObjectsOfNumbers = {
    ...getTagsObjectBase(),
    ...tagPercentsWithUnknownUncoll,
    producing: {
      ...tagPercentsWithUnknownUncoll.producing,
      Producing: totalTime === 0 ? 0 : untaggedProducingTime / totalTime,
    },
  };

  // use percentWidths to expand bar widths
  // widths denominator is the largest tag that isn't producing, no production scheduled, uncollected
  const categoriesNotAffectingBarWidths = [
    ...CATEGORIES_NOT_AFFECTING_BAR_WIDTHS,
    // calculate "producing" widths if we have producing tags
    ...(isChartWithProducingTags ? [] : ["producing"]),
    // also ignore all hidden categories (e.g. for customized layouts)
    ...hiddenCategories,
  ];

  // biggest bar width becomes the denominator for the others' widths
  const widthsDenominator: number = Object.entries(categoriesWithTagsTimes)
    .reduce((accum, [category, tagObj]) => {
      if (categoriesNotAffectingBarWidths.includes(category)) {
        return accum;
      }
      const tagTimes = Object.values(tagObj);
      return [...accum, ...tagTimes];
    }, [] as any)
    // use Math.abs to count negative times as positive widths
    .reduce((max, cur) => Math.max(max, Math.abs(cur)), 0);

  // convert all tag times to tag percent widths by dividing by largest tag time
  // e.g. {producing: {}, unplanned: {Bundler: 0.198495163}, ...}
  const tagPercentWidths: PnlCategoryObjOfObjectsOfNumbers = Object.entries(
    categoriesWithTagsTimes
  ).reduce(
    (
      accum,
      [category, tagsTotalDurationsObj]: [string, PnlsOrTagsWithMeasurementObj]
    ) => {
      accum[category] = Object.entries(tagsTotalDurationsObj).reduce(
        (tagsObjAcc, [tag, tagTime]) => {
          tagsObjAcc[tag] = +tagTime / widthsDenominator;
          return tagsObjAcc;
        },
        {} as PnlsOrTagsWithMeasurementObj
      );
      return accum;
    },
    {} as PnlCategoryObjOfObjectsOfNumbers
  );

  const tagPercentWidthsWithUnknownUncoll: PnlCategoryObjOfObjectsOfNumbers = {
    ...getTagsObjectBase(),
    ...tagPercentWidths,
    unknown: {
      Unknown: !rollup.unknown
        ? 0
        : widthsDenominator === 0 // if widths denom is 0, that means unknown is the only category affecting width
          ? 1
          : rollup.unknown / widthsDenominator,
    },
    uncollected: {
      Uncollected: !rollup.uncollected
        ? 0
        : rollup.uncollected / widthsDenominator,
    },
  };

  const tagPercentWidthsWithProdUnknownUncoll: PnlCategoryObjOfObjectsOfNumbers =
    {
      ...tagPercentWidthsWithUnknownUncoll,
      // if we have producing tags, subtract their width from non-tagged Producing bar
      producing: {
        ...tagPercentWidthsWithUnknownUncoll.producing,
        ...(untaggedProducingTime > 0
          ? {
              Producing: untaggedProducingTime / widthsDenominator,
            }
          : {}),
      },
    };

  return SORTED_STANDARD_PNL_CATEGORIES.map(category => {
    const categoryTags = rollup.tags[category]
      ? Object.entries(rollup.tags[category])
      : [];
    const categoryTotalDuration = rollup[category] ? rollup[category] : 0;
    const tagsPercentsObject: PnlsOrTagsWithMeasurementObj =
      tagPercentsWithProdUnknownUncoll[category];

    const bars = Object.entries(tagsPercentsObject)
      .map(([tag, tagPercent]) => {
        const camelCaseTag = camelCase(tag);
        const doesCategoryHaveTags =
          categoryTags &&
          categoryTags.length > 0 &&
          ![
            "unknown",
            "uncollected",
            isChartWithProducingTags ? null : "producing",
            // ...speedTags
          ].includes(camelCaseTag);

        const tagTime =
          doesCategoryHaveTags && camelCaseTag === "producing"
            ? untaggedProducingTime
            : doesCategoryHaveTags
              ? (categoryTags.find(([tagName, _]) => tagName === tag) || [
                  "",
                  0,
                ])[1]
              : camelCaseTag === "producing"
                ? isChartWithProducingTags
                  ? (categoryTags.find(([tagName, _]) => tagName === tag) || [
                      "",
                      0,
                    ])[1]
                  : untaggedProducingTime
                : categoryTotalDuration;

        return {
          title: tag,
          category,
          count: null,
          color: PNL_COLORS[category],
          time: tagTime,
          percent: tagPercent,
          percentWidth: tagPercentWidthsWithProdUnknownUncoll[category][tag],
          numberOfEvents: dataDumpFullRange?.pnlLosses[category].numberOfEvents,
          averageDurationMs:
            dataDumpFullRange?.pnlLosses[category].averageDurationMs,
        };
      })
      .sort((a, b) => (a.percent > b.percent ? -1 : 1));
    return {
      title: PnlCategoryLabelsEnum[category],
      time: categoryTotalDuration,
      count: null,
      percent: sumArray(Object.values(tagsPercentsObject)),
      bars,
    };
  });
};
